import { Fragment, useCallback, useMemo, useState } from 'react';
import { groupBy } from 'lodash-es';

import styled from '@emotion/styled';
import {
  ButtonGhost,
  FilterChip,
  filterChipClasses,
  Grid,
  SegmentedControl,
  Select,
  Switch,
  TextFilter,
} from '@kc-console/common-ui';
import { useTranslation } from '@kc-console/core';
import { IcRotateCcwRegular } from '@kep/kcds-icons';

import {
  getFilterDefMap,
  getIsFilterApplied,
  isCollectionFilterDef,
  isDateFilterDef,
  isDateRangeFilterDef,
  isDateTimeRangeFilterDef,
  isPropertyFilterDef,
  isSegmentFilterDef,
  isSelectFilterOptionDef,
  isSwitchFilterDef,
  isTextFilterDef,
  isTextFilterOptionDef,
} from '../DataTable/utils';
import ActionBarDateFilter from './ActionBarDateFilter';
import ActionBarDateRangeFilter from './ActionBarDateRangeFilter';
import ActionBarDateTimeRangeFilter from './ActionBarDateTimeRangeFilter';
import ActionBarFilterPopup from './ActionBarFilterPopup';
import ActionBarPropertyFilter from './ActionBarPropertyFilter';
import { actionBarClasses, getIsFilterDisabled } from './utils';

import type { JSX } from 'react';
import type { SegmentedControlOption, SelectProps, SelectValue } from '@kc-console/common-ui';
import type {
  CollectionFilterDef,
  DateFilterDef,
  DateRangeFilterDef,
  DateTimeRangeFilterDef,
  FilterDefBase,
  FilterState,
  PropertyFilterDef,
  SegmentFilterDef,
  SwitchFilterDef,
  TextFilterDef,
} from '../DataTable/types';
import type { PropertyFilterType } from './utils';

function parseDate(value: unknown): Date | null {
  if (value instanceof Date) {
    return value;
  }
  if (typeof value === 'string' || typeof value === 'number') {
    const date = new Date(value);
    return isNaN(date.getTime()) ? null : date;
  }
  return null;
}

function parseDateRange(value: unknown): [Date, Date] | null {
  if (!Array.isArray(value) || value.length !== 2) {
    return null;
  }

  const [start, end] = value;
  const startDate = start instanceof Date ? start : new Date(start?.toString() ?? '');
  const endDate = end instanceof Date ? end : new Date(end?.toString() ?? '');

  if (isNaN(startDate.getTime()) || isNaN(endDate.getTime())) {
    return null;
  }

  return [startDate, endDate];
}

export interface ActionBarFilterContentProps {
  filterDefs: FilterDefBase[];
  filters: FilterState[];
  onFiltersChange: (filter: FilterState[]) => void;
  autoFocusPropertyFilter?: boolean;
  id?: string;
  /**
   * 초기 필터 기준값입니다. 현재 필터가 이 값과 다를 때만 초기화 버튼을 표시하며,
   * 초기화 시 이 값으로 복원합니다.
   */
  initialFilters?: FilterState[];
  propertyFilterType?: PropertyFilterType;
}

export type FilterDefDisplayedAsFilterChip = PropertyFilterDef;

export default function ActionBarFilterContent(props: ActionBarFilterContentProps) {
  const { t } = useTranslation();
  const {
    filters,
    onFiltersChange,
    filterDefs,
    id,
    initialFilters,
    autoFocusPropertyFilter,
    propertyFilterType,
  } = props;

  const [filterPopup, setFilterPopup] = useState<{
    anchorEl: HTMLElement;
    filterDef: FilterDefBase;
    filterIndex: number;
  } | null>(null);

  const [dateFilterResetKey, setDateFilterResetKey] = useState(0);

  const shouldShowFilterReset = useMemo(
    () => getIsFilterApplied(filters, initialFilters, filterDefs),
    [filterDefs, filters, initialFilters],
  );

  const getFiltersByKey = useMemo(() => {
    const filtersDictByKey = groupBy(filters, filter => filter.key);
    return (key: string): FilterState[] => filtersDictByKey[key] ?? [];
  }, [filters]);

  const getFilterDefByKey = useMemo(() => {
    const map = filterDefs.reduce(
      (map, filterDef) => {
        if (isPropertyFilterDef(filterDef)) {
          filterDef.options.forEach(option => {
            map[option.id] = filterDef;
          });
        } else if (filterDef.id) {
          map[filterDef.id] = filterDef;
        }
        return map;
      },
      {} as Record<string, FilterDefBase>,
    );
    return (key: string): FilterDefBase | null => {
      return map[key] ?? null;
    };
  }, [filterDefs]);

  const getPropertyFilterDefOption = useMemo(() => {
    const map = getFilterDefMap(filterDefs);

    return (filter: FilterState) => {
      const filterDef = map[filter.key] as FilterDefBase | undefined;
      if (
        !filterDef ||
        (!isTextFilterOptionDef(filterDef) && !isSelectFilterOptionDef(filterDef))
      ) {
        return null;
      }
      return filterDef;
    };
  }, [filterDefs]);

  const getIsFilterDisplayedAsFilterChip = (filter: FilterState) => {
    return filterDefs.some(
      filterDef =>
        isPropertyFilterDef(filterDef) &&
        filterDef.options.some(option => option.id === filter.key),
    );
  };

  const handleFilterAdd = (newFilter: FilterState) => {
    const exists = getFiltersByKey(newFilter.key);
    if (exists.some(filter => filter.value === newFilter.value)) {
      // 중복 방지
      return;
    }
    onFiltersChange([...filters, newFilter]);
  };

  const handleFilterRemove = (filterIndex: number) => {
    onFiltersChange(filters.filter((_, i) => i !== filterIndex));
  };

  const handleFilterReset = () => {
    onFiltersChange(initialFilters ?? []);
    setDateFilterResetKey(key => key + 1);
  };

  const handleTextFilterUpdate = (newFilter: FilterState) => {
    const nextFilters = getFiltersByKey(newFilter.key).length
      ? newFilter.value
        ? filters.map(filter => (filter.key === newFilter.key ? newFilter : filter))
        : filters.filter(filter => filter.key !== newFilter.key)
      : filters.concat(newFilter);
    onFiltersChange(nextFilters);
  };

  const handleCollectionFilterUpdate = (key: string, newFilters: FilterState[]) => {
    const nextFilters = filters.filter(filter => filter.key !== key).concat(newFilters);
    onFiltersChange(nextFilters);
  };

  const handleSwitchFilterChange = (key: string, checked: boolean) => {
    const nextFilters = filters.filter(filter => filter.key !== key);
    if (checked) {
      nextFilters.push({ key, value: true });
    }
    onFiltersChange(nextFilters);
  };

  const handlePropertyFilterSubmit = (values: { filterKey: string; filterValue: unknown }) => {
    handleFilterAdd({ key: values.filterKey, value: values.filterValue });
  };

  const handlePropertyFilterChange = (
    filterIndex: number,
    values: { inputValue: unknown; propertyValue: string },
  ) => {
    const nextFilters = filters.map((filter, i) =>
      i === filterIndex ? { key: values.propertyValue, value: values.inputValue } : filter,
    );
    onFiltersChange(nextFilters);
  };

  const handleSegmentFilterChange = (key: string, value: unknown) => {
    const nextFilters = filters.filter(filter => filter.key !== key).concat({ key, value });
    onFiltersChange(nextFilters);
  };

  const handleDateFilterChange = (key: string, value: Date | null) => {
    const nextFilters = filters.filter(filter => filter.key !== key);
    if (value) {
      nextFilters.push({ key, value });
    }
    onFiltersChange(nextFilters);
  };

  const handleDateRangeFilterChange = (key: string, value: [Date, Date] | null) => {
    const nextFilters = filters.filter(filter => filter.key !== key).concat({ key, value });
    onFiltersChange(nextFilters);
  };

  const isFilterDisabled = useCallback(
    (filterDef: FilterDefBase) => {
      return getIsFilterDisabled(filterDef, filters);
    },
    [filters],
  );

  const renderPropertyFilter = (filterDef: PropertyFilterDef) => {
    const disabled = isFilterDisabled(filterDef);

    return (
      <ActionBarPropertyFilter
        autoFocus={autoFocusPropertyFilter}
        filterDef={filterDef}
        filters={filters}
        onSubmit={handlePropertyFilterSubmit}
        type={propertyFilterType}
        disabled={disabled}
      />
    );
  };

  const renderTextFilter = (filterDef: TextFilterDef) => {
    const exists = getFiltersByKey(filterDef.id);
    const disabled = isFilterDisabled(filterDef);

    return (
      <TextFilter
        filter={exists.at(0)?.value ?? ''}
        onFilterChange={value =>
          handleTextFilterUpdate({ key: filterDef.id, value: String(value) })
        }
        triggerOption="entered"
        placeholder={filterDef.placeholder ?? t('common:common.prompt.enter_value')}
        disabled={disabled}
        disabledClearable={filterDef.disabledClearable}
      />
    );
  };

  const renderSwitchFilter = (filterDef: SwitchFilterDef) => {
    const exists = getFiltersByKey(filterDef.id);
    const currentValue =
      exists.length > 0 ? Boolean(exists[0].value) : (filterDef.defaultValue ?? false);
    const disabled = isFilterDisabled(filterDef);

    return (
      <Switch
        checked={currentValue}
        onChange={(e, checked) => handleSwitchFilterChange(filterDef.id, checked)}
        text={filterDef.label}
        size="md"
        disabled={disabled}
      />
    );
  };

  const renderCollectionFilter = (filterDef: CollectionFilterDef) => {
    const filterKey = filterDef.id;
    const filterKeyLabel = filterDef.label ?? filterKey;
    const placeholder =
      filterDef.placeholder ?? filterDef.label ?? t('ui:common.select.required.ph');
    const prevFilters = filters.filter(filter => filter.key === filterKey);
    const options = filterDef.options.map(option => ({
      endBadge: option.endBadge,
      subTexts: option.subTexts,
      value: option.value,
      text: option.label,
    }));

    const searchable = filterDef.searchable ?? options.length >= 10;
    const confirmable = filterDef.confirmable ?? false;
    const selectionControls = filterDef.selectionControls ?? undefined;

    const disabled = isFilterDisabled(filterDef);

    if (filterDef.enableMultiSelect) {
      const value = prevFilters.map(filter => filter.value);

      return (
        <CollectionFilterSelect
          multiple
          searchable={searchable}
          confirmable={confirmable}
          options={options}
          value={value}
          onChange={value =>
            handleCollectionFilterUpdate(
              filterKey,
              value.map(v => ({ key: filterKey, value: v })),
            )
          }
          renderValue={value => `${filterKeyLabel} (${value.length})`}
          placeholder={placeholder}
          size="md"
          selectionControls={selectionControls}
          slotProps={{
            footer: {
              applyLabel: t('common:common.action.apply'),
            },
          }}
          disabled={disabled}
        />
      );
    }

    const value = prevFilters.map(filter => filter.value).at(0) ?? '';
    return (
      <CollectionFilterSelect
        searchable={searchable}
        options={options}
        value={value}
        onChange={newValue => {
          if (newValue === value) {
            handleCollectionFilterUpdate(filterKey, []);
            return;
          }
          handleCollectionFilterUpdate(filterKey, [{ key: filterKey, value: newValue }]);
        }}
        renderValue={value => {
          const selectedOption = options.find(option => option.value === value);
          return `${selectedOption ? selectedOption.text : t('ui:form.select.none')}`;
        }}
        placeholder={placeholder}
        size="md"
        disabled={disabled}
      />
    );
  };

  const renderSegmentFilter = (filterDef: SegmentFilterDef) => {
    const options: SegmentedControlOption<unknown>[] = filterDef.options.map(option => ({
      value: option.value,
      label: option.label,
    }));
    const key = filterDef.id;
    const exists = getFiltersByKey(key);
    const value = exists.at(0)?.value;
    const disabled = isFilterDisabled(filterDef);

    return (
      <SegmentedControl
        options={options}
        value={value}
        onChange={(event, value) => handleSegmentFilterChange(key, value)}
        disabled={disabled}
      />
    );
  };

  const renderDateFilter = (filterDef: DateFilterDef) => {
    const key = filterDef.id;
    const exists = getFiltersByKey(key);
    const value = exists.at(0)?.value;
    const safeValue = parseDate(value);
    const disabled = isFilterDisabled(filterDef);
    const placeholder =
      filterDef.placeholder ?? filterDef.label ?? t('ui:common.select.required.ph');

    const handleChange = (value: Date | null) => {
      handleDateFilterChange(key, value);
    };

    return (
      <ActionBarDateFilter
        key={`${filterDef.id}-${dateFilterResetKey}`}
        onChange={handleChange}
        value={safeValue}
        placeholder={placeholder}
        minDate={filterDef.minDate ? new Date(filterDef.minDate) : undefined}
        maxDate={filterDef.maxDate ? new Date(filterDef.maxDate) : undefined}
        disabled={disabled}
      />
    );
  };

  const renderDateRangeFilter = (filterDef: DateRangeFilterDef) => {
    const key = filterDef.id;
    const exists = getFiltersByKey(key);
    const value = exists.at(0)?.value;
    const safeValue = parseDateRange(value);
    const disabled = isFilterDisabled(filterDef);
    const placeholder =
      filterDef.placeholder ?? filterDef.label ?? t('ui:common.select.required.ph');

    const handleChange = (value: [Date, Date] | null) => {
      handleDateRangeFilterChange(key, value);
    };

    return (
      <ActionBarDateRangeFilter
        key={`${filterDef.id}-${dateFilterResetKey}`}
        onChange={handleChange}
        value={safeValue}
        disabled={disabled}
        placeholder={placeholder}
        minDate={filterDef.minDate ? new Date(filterDef.minDate) : undefined}
        maxDate={filterDef.maxDate ? new Date(filterDef.maxDate) : undefined}
      />
    );
  };

  const renderDateTimeRangeFilter = (filterDef: DateTimeRangeFilterDef) => {
    const key = filterDef.id;
    const exists = getFiltersByKey(key);
    const value = exists.at(0)?.value;
    const safeValue = parseDateRange(value);
    const disabled = isFilterDisabled(filterDef);
    const placeholder =
      filterDef.placeholder ?? filterDef.label ?? t('ui:common.select.required.ph');

    const handleChange = (value: [Date, Date] | null) => {
      handleDateRangeFilterChange(key, value);
    };

    return (
      <ActionBarDateTimeRangeFilter
        key={`${filterDef.id}-${dateFilterResetKey}`}
        onChange={handleChange}
        value={safeValue}
        disabled={disabled}
        placeholder={placeholder}
        showSeconds={filterDef.showSeconds}
        showTimezone={filterDef.showTimezone}
        allowEqualDates={filterDef.allowEqualDates}
        minDate={filterDef.minDate ? new Date(filterDef.minDate) : undefined}
        maxDate={filterDef.maxDate ? new Date(filterDef.maxDate) : undefined}
      />
    );
  };

  const renderFilter = (filterDef: FilterDefBase) => {
    if (isPropertyFilterDef(filterDef)) {
      return renderPropertyFilter(filterDef);
    }
    if (isTextFilterDef(filterDef) || isTextFilterOptionDef(filterDef)) {
      return renderTextFilter(filterDef);
    }
    if (isCollectionFilterDef(filterDef)) {
      return renderCollectionFilter(filterDef);
    }
    if (isSwitchFilterDef(filterDef)) {
      return renderSwitchFilter(filterDef);
    }
    if (isSegmentFilterDef(filterDef)) {
      return renderSegmentFilter(filterDef);
    }
    if (isDateFilterDef(filterDef)) {
      return renderDateFilter(filterDef);
    }
    if (isDateRangeFilterDef(filterDef)) {
      return renderDateRangeFilter(filterDef);
    }
    if (isDateTimeRangeFilterDef(filterDef)) {
      return renderDateTimeRangeFilter(filterDef);
    }
    return null;
  };

  return (
    <Grid
      id={id}
      className={actionBarClasses.filterContent}
      container
      gap="8px"
      alignItems="center"
    >
      {filterDefs.map((filterDef, filterDefIndex) => (
        <Fragment key={filterDefIndex}>{renderFilter(filterDef)}</Fragment>
      ))}
      {shouldShowFilterReset && (
        <ButtonGhost
          size="sm"
          variant="accent"
          startIcon={<IcRotateCcwRegular />}
          onClick={handleFilterReset}
        >
          {t('ui:action.reset')}
        </ButtonGhost>
      )}
      {filters.map((filter, filterIndex) => {
        if (!getIsFilterDisplayedAsFilterChip(filter)) return null;
        const filterDef = getFilterDefByKey(filter.key);
        const propertyFilterDefOption = getPropertyFilterDefOption(filter);
        if (!filterDef || !propertyFilterDefOption) return null;

        const propertyFilterDefOptionSelectOptions =
          (propertyFilterDefOption.type === 'select' && propertyFilterDefOption.options) || [];
        const filterKeyLabel = propertyFilterDefOption.label ?? filter.value;
        const filterOperatorLabel = ':';
        const filterValueLabel =
          propertyFilterDefOptionSelectOptions.find(option => option.value === filter.value)
            ?.label ?? filter.value;
        return (
          <ActionBarFilterChip
            key={filterIndex}
            label={`${filterKeyLabel} ${filterOperatorLabel} ${filterValueLabel}`}
            onClickLabel={event =>
              setFilterPopup({ anchorEl: event.currentTarget, filterIndex, filterDef: filterDef })
            }
            onDelete={() => handleFilterRemove(filterIndex)}
          />
        );
      })}
      <ActionBarFilterPopup
        open={!!filterPopup}
        anchorEl={filterPopup?.anchorEl}
        filterDef={filterPopup?.filterDef}
        filterIndex={filterPopup?.filterIndex}
        filters={filters}
        onClose={() => setFilterPopup(null)}
        onSubmit={(filterIndex, filterKey, filterValue) => {
          handlePropertyFilterChange(filterIndex, {
            propertyValue: filterKey,
            inputValue: filterValue,
          });
          setFilterPopup(null);
        }}
      />
    </Grid>
  );
}

const ActionBarFilterChip = styled(FilterChip)`
  max-width: 600px;

  .${filterChipClasses.label} {
    word-break: break-all;
  }
`;

const CollectionFilterSelect = styled(Select)`
  min-width: 72px;
  max-width: 320px;
` as <
  OptionValue = unknown,
  Multiple extends boolean = false,
  Value = SelectValue<OptionValue, Multiple>,
>(
  props: SelectProps<OptionValue, Multiple, Value>,
) => JSX.Element;
